import { EMPTY_ARR } from '@dadohr/shared/domainTypes/emptyConstants';
import { ButtonBase, Paper, PaperProps, Tooltip } from '@material-ui/core';
import CircularProgress from '@material-ui/core/CircularProgress';
import TextField, { TextFieldProps } from '@material-ui/core/TextField';
import { FilterOptionsState } from '@material-ui/lab';
import Autocomplete, {
  AutocompleteGetTagProps,
  AutocompleteRenderOptionState,
  createFilterOptions
} from '@material-ui/lab/Autocomplete';
import { isArray } from 'lodash';
import { PlusSquare } from 'lucide-react';
import React, {
  createElement,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useUnsafeTranslation } from '../../i18next';
import { toSearchTermRegexp } from '../../services/user';
import { useTheme } from '../../themes';
import { TokenDefinition, TokenSelectorDialog } from '../MarkdownText';

const simpleEquality = <T extends unknown>(a: T, b: T) => a === b;
const identity = <T extends unknown>(t: T) => t;

const makePopperFooterPaperComponent = (
  popperFooter: React.ReactNode
): React.ComponentType<PaperProps> => {
  const AutocompleteFooterPaper = ({ children, ...props }: PaperProps) => (
    <Paper {...props}>
      {children}
      <div onMouseDown={(e) => e.preventDefault()}>{popperFooter}</div>
    </Paper>
  );
  AutocompleteFooterPaper.displayName = 'AutocompleteFooterPaper';
  return AutocompleteFooterPaper;
};

type SharedProps<T> = {
  label?: React.ReactNode;
  placeholder?: string;
  fullWidth?: boolean;
  required?: boolean;
  readOnly?: boolean;
  disabledClearable?: boolean;
  size?: 'small' | 'medium';
  options: T[] | (() => Promise<T[]>);
  getOptionSelected?: (option: T, value: T) => boolean;
  getOptionLabel?: (option: T) => any;
  renderOption?: (
    option: T,
    state: AutocompleteRenderOptionState
  ) => React.ReactNode;
  filterOptions?: (options: T[], state: FilterOptionsState<T>) => T[];
  autoFocus?: boolean;
  openOnFocus?: boolean;
  style?: React.CSSProperties;
  inputProps?: TextFieldProps['inputProps'];
  InputProps?: TextFieldProps['InputProps'];
  InputLabelProps?: TextFieldProps['InputLabelProps'];
  error?: boolean;
  helperText?: React.ReactNode;
  onBlur?: () => void;
};

export const AutocompleteSingleFreeSolo = ({
  value,
  onChange,
  label,
  placeholder,
  fullWidth,
  required,
  readOnly,
  disabledClearable,
  size,
  options,
  getOptionSelected = simpleEquality,
  getOptionLabel = identity,
  renderOption,
  filterOptions,
  groupBy,
  autoFocus,
  openOnFocus,
  style,
  inputProps,
  InputProps,
  error,
  helperText,
  onBlur
}: {
  value: string;
  onChange: (nextValue: string) => void;
  groupBy?: (option: string) => string;
} & SharedProps<string>) => {
  const isAsync = typeof options === 'function';
  const [open, setOpen] = useState(false);
  const [opts, setOpts] = React.useState<string[] | null>(
    readOnly || isAsync ? null : (options as string[])
  );

  useEffect(() => {
    if (!readOnly && open && opts === null && typeof options === 'function') {
      (options as () => Promise<string[]>)().then(setOpts);
    }
  }, [open, opts, options, readOnly]);

  useEffect(() => {
    if (typeof options !== 'function') {
      setOpts(options);
    }
  }, [options]);

  const loading = open && opts === null && !readOnly;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const startingValue = useMemo(() => value, []);

  return (
    <Autocomplete
      multiple={false}
      defaultValue={startingValue}
      inputValue={value}
      onInputChange={(ev, v) => onChange(v)}
      open={open}
      openOnFocus={openOnFocus}
      freeSolo={true}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      getOptionSelected={getOptionSelected}
      getOptionLabel={getOptionLabel}
      options={opts || EMPTY_ARR}
      size={size}
      loading={loading}
      disableClearable={readOnly || disabledClearable}
      fullWidth={fullWidth}
      filterOptions={filterOptions}
      renderOption={renderOption}
      groupBy={groupBy}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          variant="outlined"
          fullWidth={fullWidth}
          required={required}
          autoFocus={autoFocus}
          style={style}
          inputProps={{ ...params.inputProps, ...inputProps }}
          InputProps={{
            ...params.InputProps,
            ...InputProps,
            readOnly,
            endAdornment: (
              <React.Fragment>
                {loading ? (
                  <CircularProgress color="inherit" size={20} />
                ) : null}
                {params.InputProps.endAdornment}
              </React.Fragment>
            )
          }}
          error={error}
          helperText={helperText}
          onBlur={onBlur}
        />
      )}
    />
  );
};

export const AutocompleteSingle = <T extends unknown>({
  value,
  onChange,
  label,
  placeholder,
  fullWidth,
  required,
  readOnly,
  disabledClearable,
  size,
  options,
  getOptionSelected = simpleEquality,
  getOptionLabel = identity,
  renderOption,
  filterOptions,
  autoFocus,
  openOnFocus,
  style,
  maxWidth,
  inputProps,
  InputProps,
  error,
  helperText,
  onBlur,
  disabled,
  popperFooter
}: {
  value: T | null;
  disabled?: boolean | undefined;
  onChange: (nextValue: T | null) => void;
  maxWidth?: number | string;
  popperFooter?: React.ReactNode;
} & SharedProps<T>) => {
  const isAsync = typeof options === 'function';
  const [open, setOpen] = useState(false);
  const [opts, setOpts] = React.useState<T[] | null>(
    readOnly || isAsync ? null : (options as T[])
  );

  useEffect(() => {
    if (!readOnly && open && opts === null && typeof options === 'function') {
      (options as () => Promise<T[]>)().then(setOpts);
    }
  }, [open, opts, options, readOnly]);

  // TODO Enroll in Experience - check if this has adverse effects
  useEffect(() => {
    if (isArray(options)) {
      setOpts(options);
    }
  }, [options]);

  const loading = open && opts === null && !readOnly;

  const PaperComponent = useMemo(
    () =>
      popperFooter ? makePopperFooterPaperComponent(popperFooter) : undefined,
    [popperFooter]
  );

  return (
    <Autocomplete
      multiple={false}
      value={value}
      onChange={(ev, nextValue) => onChange(nextValue)}
      open={open}
      openOnFocus={openOnFocus}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      getOptionSelected={getOptionSelected}
      getOptionLabel={getOptionLabel}
      options={opts || EMPTY_ARR}
      size={size}
      fullWidth={fullWidth}
      disabled={disabled}
      loading={loading}
      style={maxWidth ? { maxWidth } : undefined}
      disableClearable={readOnly || disabledClearable}
      filterOptions={filterOptions}
      renderOption={renderOption}
      PaperComponent={PaperComponent}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          variant="outlined"
          fullWidth={fullWidth}
          required={required}
          autoFocus={autoFocus}
          style={style}
          inputProps={{ ...params.inputProps, ...inputProps }}
          InputProps={{
            ...params.InputProps,
            ...InputProps,
            readOnly,
            endAdornment: (
              <React.Fragment>
                {loading ? (
                  <CircularProgress color="inherit" size={20} />
                ) : null}
                {params.InputProps.endAdornment}
              </React.Fragment>
            )
          }}
          error={error}
          helperText={helperText}
          onBlur={onBlur}
        />
      )}
    />
  );
};

export const AutocompleteSingleSimpleKeyed = <T extends any, X extends string>({
  value,
  onChange,
  options,
  toSelectKey,
  toLabel,
  label,
  placeholder,
  autoFocus,
  fullWidth,
  onBlur
}: {
  value: X;
  onChange: (nextValue: X) => void;
  options: T[];
  toSelectKey: (o: T) => X;
  toLabel: (o: T) => string;
  label?: string;
  placeholder?: string;
  autoFocus?: boolean;
  fullWidth?: boolean;
  onBlur?: () => void;
}) => {
  const selected = options.find((o) => toSelectKey(o) === value) || null;
  return (
    <AutocompleteSingle
      label={label}
      placeholder={placeholder}
      value={selected}
      onChange={(o) => o !== null && onChange(toSelectKey(o))}
      options={options}
      getOptionSelected={(a, b) => toSelectKey(a) === toSelectKey(b)}
      getOptionLabel={(a) => toLabel(a)}
      filterOptions={(os, s) => {
        const re = toSearchTermRegexp(s.inputValue);
        return os.filter((o) => toLabel(o).match(re));
      }}
      fullWidth={fullWidth}
      autoFocus={autoFocus}
      disabledClearable
      onBlur={onBlur}
    />
  );
};

const AddInviteeToken = ({
  value,
  tokens,
  onChange
}: Omit<TextFieldProps, 'variant' | 'onChange'> & {
  value: string[];
  tokens: TokenDefinition[];
  onChange: (nextValue: string[]) => void;
}) => {
  const ref = useRef<HTMLInputElement>(null);
  const theme = useTheme();
  const { t } = useUnsafeTranslation();
  const [lastSelection, setLastSelection] = useState<[number, number] | null>(
    null
  );
  const [dialogState, setDialogState] = useState(false);
  return (
    <>
      <ButtonBase
        onClick={() => {
          const input = ref.current;
          if (input) {
            setLastSelection([
              input.selectionStart || 0,
              input.selectionEnd === null
                ? input.selectionStart || 0
                : input.selectionEnd
            ]);
          }
          return setDialogState(true);
        }}
      >
        <Tooltip title={`${t('Insert text variable')}`}>
          <PlusSquare
            size={16}
            strokeWidth={1.8}
            color={theme.custom.colors.gray.w500}
          />
        </Tooltip>
      </ButtonBase>
      <TokenSelectorDialog
        open={!!dialogState}
        onClose={() => {
          console.log(lastSelection);
          setDialogState(false);
          setTimeout(() => {
            if (lastSelection && ref.current) {
              ref.current.focus();
              ref.current.setSelectionRange(...lastSelection);
            }
          });
        }}
        tokens={tokens}
        onSelect={(t, opts) => {
          if (!dialogState) {
            return;
          }
          setTimeout(() => {
            setDialogState(false);
            const token = `{{ ${[t.token, opts]
              .filter((x) => x)
              .join(' | ')} }}`;
            const input = ref.current;
            if (lastSelection) {
              const characters = value;
              const [start, end] = lastSelection;
              const nextValue = characters.splice(start, end - start, token);
              onChange(nextValue);
              setTimeout(() => {
                if (input) {
                  // should always be defined
                  ref.current.focus();
                  const nextCursor = start + token.length;
                  ref.current.setSelectionRange(nextCursor, nextCursor);
                  ref.current.focus();
                }
              });
            } else {
              const nextValue = [...value, token];
              onChange(nextValue);
              setTimeout(() => {
                if (input) {
                  ref.current.focus();
                  ref.current.setSelectionRange(
                    nextValue.length,
                    nextValue.length
                  );
                  ref.current.focus();
                }
              });
            }
          });
        }}
      />
    </>
  );
};

export const AutocompleteMultiFreeSolo = ({
  value,
  onChange,
  label,
  placeholder,
  fullWidth,
  required,
  readOnly,
  disabledClearable,
  disableCloseOnSelect,
  size,
  options,
  getOptionSelected = simpleEquality,
  getOptionLabel = identity,
  renderOption,
  filterOptions,
  renderTags,
  autoFocus,
  openOnFocus,
  style,
  inputProps,
  InputLabelProps,
  error,
  helperText,
  onBlur,
  tokens,
  allowTokens
}: {
  value: string[];
  onChange: (nextValue: string[]) => void;
  disableCloseOnSelect?: boolean;
  tokens?: TokenDefinition[];
  allowTokens?: boolean;
  renderTags?: (
    value: string[],
    getTagProps: AutocompleteGetTagProps
  ) => React.ReactNode;
} & SharedProps<string>) => {
  const isAsync = typeof options === 'function';
  const [open, setOpen] = useState(false);
  const [opts, setOpts] = React.useState<string[] | null>(
    readOnly || isAsync ? null : (options as string[])
  );

  useEffect(() => {
    if (!readOnly && open && opts === null && typeof options === 'function') {
      (options as () => Promise<string[]>)().then(setOpts);
    }
    if (!readOnly && open && typeof options !== 'function') {
      setOpts(options);
    }
  }, [open, opts, options, readOnly]);

  const loading = open && opts === null && !readOnly;

  return (
    <Autocomplete
      multiple
      freeSolo={true}
      value={value}
      onChange={(ev: any, nextValue: string[] | null) =>
        onChange(nextValue || [])
      }
      open={open}
      openOnFocus={openOnFocus}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      getOptionSelected={getOptionSelected}
      getOptionLabel={getOptionLabel}
      options={opts || EMPTY_ARR}
      loading={loading}
      size={size}
      fullWidth={fullWidth}
      filterOptions={filterOptions}
      disableCloseOnSelect={disableCloseOnSelect}
      disableClearable={readOnly || disabledClearable || allowTokens}
      renderOption={renderOption}
      renderTags={renderTags}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          variant="outlined"
          fullWidth={fullWidth}
          required={required}
          autoFocus={autoFocus}
          style={style}
          inputProps={{ ...params.inputProps, ...inputProps }}
          InputLabelProps={{ ...params.InputLabelProps, shrink: value.length > 0 || undefined, ...InputLabelProps }}
          InputProps={{
            ...params.InputProps,
            readOnly,
            endAdornment: (
              <>
                {params.InputProps.endAdornment}
                {loading ? (
                  <CircularProgress color="inherit" size={20} />
                ) : allowTokens ? (
                  <AddInviteeToken
                    tokens={tokens || []}
                    value={value}
                    onChange={(ev) => {
                      return onChange(ev);
                    }}
                  />
                ) : null}
              </>
            )
          }}
          error={error}
          helperText={helperText}
          onBlur={onBlur}
        />
      )}
    />
  );
};

export const AutocompleteMulti = <T extends unknown>({
  value,
  onChange,
  label,
  placeholder,
  fullWidth,
  required,
  readOnly,
  disabledClearable,
  disableCloseOnSelect,
  size,
  options,
  getOptionSelected = simpleEquality,
  getOptionLabel = identity,
  renderOption,
  filterOptions,
  renderTags,
  autoFocus,
  openOnFocus,
  style,
  inputProps,
  error,
  helperText,
  onBlur
}: {
  value: T[];
  onChange: (nextValue: T[]) => void;
  disableCloseOnSelect?: boolean;
  renderTags?: (
    value: T[],
    getTagProps: AutocompleteGetTagProps
  ) => React.ReactNode;
} & SharedProps<T>) => {
  const isAsync = typeof options === 'function';
  const [open, setOpen] = useState(false);
  const [opts, setOpts] = React.useState<T[] | null>(
    readOnly || isAsync ? null : (options as T[])
  );

  useEffect(() => {
    if (!readOnly && open && opts === null && typeof options === 'function') {
      (options as () => Promise<T[]>)().then(setOpts);
    }
    if (!readOnly && open && typeof options !== 'function') {
      setOpts(options);
    }
  }, [open, opts, options, readOnly]);

  const loading = open && opts === null && !readOnly;

  return (
    <Autocomplete
      multiple
      value={value}
      onChange={(ev: any, nextValue: T[] | null) => onChange(nextValue || [])}
      open={open}
      openOnFocus={openOnFocus}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      getOptionSelected={getOptionSelected}
      getOptionLabel={getOptionLabel}
      options={opts || EMPTY_ARR}
      loading={loading}
      size={size}
      fullWidth={fullWidth}
      filterOptions={filterOptions}
      disableCloseOnSelect={disableCloseOnSelect}
      disableClearable={readOnly || disabledClearable}
      renderOption={renderOption}
      renderTags={renderTags}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          variant="outlined"
          fullWidth={fullWidth}
          required={required}
          autoFocus={autoFocus}
          style={style}
          inputProps={{ ...params.inputProps, ...inputProps }}
          InputProps={{
            ...params.InputProps,
            readOnly,
            endAdornment: (
              <React.Fragment>
                {loading ? (
                  <CircularProgress color="inherit" size={20} />
                ) : null}
                {params.InputProps.endAdornment}
              </React.Fragment>
            )
          }}
          error={error}
          helperText={helperText}
          onBlur={onBlur}
        />
      )}
    />
  );
};

type NewOption = { __newOption: true; __inputValue: string };

export const AutocompleteMultiWithCreateOption = <T extends unknown>({
  value,
  onChange,
  label,
  placeholder,
  fullWidth,
  required,
  readOnly,
  disabledClearable,
  disableCloseOnSelect,
  size,
  options,
  getOptionSelected = simpleEquality,
  getOptionLabel = identity,
  renderOption,
  filterOptions = createFilterOptions<T>(),
  renderTags,
  renderNewOption,
  createDialog,
  autoFocus,
  style,
  inputProps,
  error,
  helperText,
  onBlur
}: {
  value: T[];
  onChange: (nextValue: T[]) => void;
  disableCloseOnSelect?: boolean;
  renderTags?: (
    value: T[],
    getTagProps: AutocompleteGetTagProps
  ) => React.ReactNode;
  renderOption: (
    option: T,
    state: AutocompleteRenderOptionState
  ) => React.ReactNode;
  renderNewOption?: (inputValue: string) => React.ReactNode;
  createDialog: React.ComponentType<{
    open: boolean;
    onClose: () => void;
    inputValue: string;
    onCreate: (newValue: T) => void;
  }>;
} & SharedProps<T>) => {
  const [dialogValue, setDialogValue] = useState('');
  const isAsync = typeof options === 'function';
  const [open, setOpen] = useState(false);
  const [opts, setOpts] = React.useState<T[] | null>(
    readOnly || isAsync ? null : (options as T[])
  );

  useEffect(() => {
    if (!readOnly && open && opts === null && typeof options === 'function') {
      (options as () => Promise<T[]>)().then(setOpts);
    }
  }, [open, opts, options, readOnly]);

  const loading = open && opts === null && !readOnly;

  return (
    <>
      <Autocomplete
        multiple
        value={value}
        onChange={(ev: any, nextValue: T[] | null) => {
          console.log('NEXT', nextValue);
          const newOption =
            nextValue && nextValue.find((o) => (o as NewOption).__newOption);
          if (newOption) {
            setDialogValue((newOption as NewOption).__inputValue);
            return;
          }
          onChange(nextValue || []);
        }}
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        getOptionSelected={getOptionSelected}
        getOptionLabel={getOptionLabel}
        options={opts || EMPTY_ARR}
        loading={loading}
        size={size}
        fullWidth={fullWidth}
        filterOptions={(options, params) => {
          const filtered = filterOptions(options, params);
          if (params.inputValue !== '') {
            const newOption: NewOption = {
              __inputValue: params.inputValue,
              __newOption: true
            };
            (filtered as any).push(newOption);
          }
          return filtered;
        }}
        disableCloseOnSelect={disableCloseOnSelect}
        disableClearable={readOnly || disabledClearable}
        renderOption={(option, state) => {
          if ((option as NewOption).__newOption) {
            const inputValue = (option as NewOption).__inputValue;
            return renderNewOption
              ? renderNewOption(inputValue)
              : `Create "${inputValue}"`;
          }
          return renderOption(option, state);
        }}
        renderTags={renderTags}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            placeholder={placeholder}
            variant="outlined"
            fullWidth={fullWidth}
            required={required}
            autoFocus={autoFocus}
            style={style}
            onBlur={onBlur}
            inputProps={{ ...params.inputProps, ...inputProps }}
            InputProps={{
              ...params.InputProps,
              readOnly,
              endAdornment: (
                <React.Fragment>
                  {loading ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </React.Fragment>
              )
            }}
            error={error}
            helperText={helperText}
          />
        )}
      />
      {dialogValue &&
        createElement(createDialog, {
          open: true,
          onClose: () => setDialogValue(''),
          onCreate: (nextValue) => {
            setDialogValue('');
            onChange([...value, nextValue]);
          },
          inputValue: dialogValue
        })}
    </>
  );
};

/**
 * @deprecated This is a bit of an outdated component. Use AutocompleteSingle instead, which also offers Create functionality now
 */
export const AutocompleteSingleWithCreateOption = <T extends any>({
  value,
  onChange,
  options,
  getOptionLabel,
  getOptionAdornment,
  label,
  renderOption,
  renderCreateOption,
  renderCreateDialog,
  fullWidth,
  alwaysShowCreateOption
}: {
  value: T;
  onChange: (nextValue: T) => void;
  label?: string;

  options: T[];
  getOptionLabel: (o: T) => string;
  getOptionAdornment?: (o: T) => React.ReactNode;
  renderOption: (o: T) => React.ReactNode;
  renderCreateOption: (inputValue: string) => React.ReactNode;
  renderCreateDialog: ({
    close,
    inputValue
  }: {
    close: () => void;
    inputValue: string;
  }) => React.ReactNode; // Should be an open dialog
  fullWidth?: boolean;
  alwaysShowCreateOption?: boolean;
}) => {
  const [dialogState, setDialogState] = useState<{ value: string } | null>(
    null
  );
  const closeDialog = () => setDialogState(null);
  const filter = useMemo(() => createFilterOptions<T>(), []);
  return (
    <>
      <Autocomplete<T, false, true, true>
        value={value as any}
        disableClearable={true}
        onChange={(event, newValue: any) => {
          if (typeof newValue === 'string') {
            // timeout to avoid instant validation of the dialog's form.
            setDialogState({ value: newValue });
          } else if (newValue && newValue.type === '__NEW__') {
            setDialogState({ value: newValue.inputValue });
          } else {
            onChange(newValue);
          }
        }}
        filterOptions={(options, params) => {
          const filtered = filter(options, params) as (
            | T
            | {
                inputValue: string;
                type: '__NEW__';
              }
          )[];

          if (alwaysShowCreateOption || params.inputValue !== '') {
            filtered.push({
              inputValue: params.inputValue,
              type: '__NEW__'
            });
          }

          return filtered as any;
        }}
        options={options}
        fullWidth={fullWidth}
        getOptionLabel={(option: any) => {
          // e.g value selected with enter, right from the input
          if (typeof option === 'string') {
            return option;
          }
          if (option.inputValue) {
            return option.inputValue;
          }
          return getOptionLabel(option);
        }}
        selectOnFocus
        handleHomeEndKeys
        renderOption={(option: any) => {
          if (option.type === '__NEW__') {
            return renderCreateOption(option.inputValue);
          }
          return renderOption(option);
        }}
        freeSolo
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            InputProps={
              getOptionAdornment && value
                ? {
                    ...params.InputProps,
                    startAdornment: getOptionAdornment(value)
                  }
                : params.InputProps
            }
            variant="outlined"
            fullWidth={fullWidth}
          />
        )}
      />
      {!!dialogState &&
        renderCreateDialog({
          close: closeDialog,
          inputValue: dialogState?.value || ''
        })}
    </>
  );
};
